import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CustomDialog from "../../components-new/common/CustomDialog";
import {
  useGetLlmFeatureConfigQuery,
  useUpdateLlmFeatureConfigMutation,
} from "../../useQuery";
import { useNotification } from "../../hooks";

const ConfigAiDialog = ({ open, onClose }) => {
  const notify = useNotification();
  const { data: llmFeatureData = [] } = useGetLlmFeatureConfigQuery(open);
  const updateLlmFeatureConfigMutation = useUpdateLlmFeatureConfigMutation();

  const [configAiRows, setConfigAiRows] = useState([]);

  const seededRef = useRef(false);

  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      setConfigAiRows((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    if (!seededRef.current && llmFeatureData.length > 0) {
      seededRef.current = true;
      setConfigAiRows(llmFeatureData);
    }
  }, [open, llmFeatureData]);

  const handleProviderChange = (id, provider) => {
    setConfigAiRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              is_openai_enabled: provider === "OpenAI" ? "true" : "false",
              is_gemini_enabled: provider === "Gemini" ? "true" : "false",
            }
          : row,
      ),
    );
  };

  const changedRows = configAiRows.filter((row) => {
    const original = llmFeatureData.find((o) => o.id === row.id);
    return (
      original &&
      (original.is_openai_enabled !== row.is_openai_enabled ||
        original.is_gemini_enabled !== row.is_gemini_enabled)
    );
  });

  const isConfigAiDirty = changedRows.length > 0;

 const handleUpdateConfigAi = async () => {
    if (changedRows.length === 0) {
      notify("No changes to update", "info");
      return;
    }

    const results = await Promise.allSettled(
      changedRows.map((row) =>
        updateLlmFeatureConfigMutation.mutateAsync({
          id: row.id,
          isOpenaiEnabled: row.is_openai_enabled,
          isGeminiEnabled: row.is_gemini_enabled,
        }),
      ),
    );

    const failed = results
      .map((res, i) => (res.status === "rejected" ? changedRows[i].nogrunt_feature : null))
      .filter(Boolean);

    if (failed.length === 0) {
      notify("Feature config updated", "success");
      onClose();
    } else {
      notify(`Failed to update: ${failed.join(", ")}`, "error");
    }
  };

  const content = (
    <Box mt={1}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose the AI provider for each feature.
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: "0.5px solid #E2E6F3", borderRadius: 2, overflow: "hidden" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F4F6FB" }}>
              <TableCell>
                <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
                  Feature
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
                  Provider
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configAiRows.length > 0 ? (
              configAiRows.map((row) => {
                const selectedProvider =
                  row.is_openai_enabled === "true"
                    ? "OpenAI"
                    : row.is_gemini_enabled === "true"
                      ? "Gemini"
                      : null;
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell>{row.nogrunt_feature}</TableCell>
                    <TableCell align="center">
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={selectedProvider}
                        onChange={(e, val) => {
                          if (val !== null) handleProviderChange(row.id, val);
                        }}
                        sx={{
                          "& .MuiToggleButton-root": {
                            textTransform: "none",
                            px: 2,
                            py: 0.4,
                            fontSize: "0.8rem",
                            border: "1px solid #e0e0e0",
                            "&.Mui-selected": {
                              backgroundColor: "#ff5336",
                              color: "#fff",
                              "&:hover": { backgroundColor: "#e64726" },
                            },
                          },
                        }}
                      >
                        <ToggleButton value="OpenAI">OpenAI</ToggleButton>
                        <ToggleButton value="Gemini">Gemini</ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No features found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const actions = (
    <>
      <Button onClick={onClose} color="inherit">
        Close
      </Button>
      {isConfigAiDirty && (
        <Button
          variant="contained"
          onClick={handleUpdateConfigAi}
          sx={{ bgcolor: "#ff5336", "&:hover": { bgcolor: "#e64726" } }}
        >
          Update
        </Button>
      )}
    </>
  );

  return (
    <CustomDialog
      open={open}
      width={600}
      onClose={onClose}
      title="Config AI"
      content={content}
      actions={actions}
    />
  );
};

export default ConfigAiDialog;